/**
 * Datumsauswahl für den Zeitstrahl (Hans 06.10.2026): immer deutsches Format
 * — unabhängig von der Spracheinstellung des Browsers, die das eingebaute
 * <input type="date"> bestimmt. Monatsblatt mit Wochenbeginn Montag; Tage
 * mit Aktivität tragen einen Punkt, Tage nach heute sind gesperrt.
 */

import { useEffect, useRef, useState } from "react"
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react"

const _monat = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" })
const _anzeige = new Intl.DateTimeFormat("de-DE", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
})
const WOCHENTAGE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]

function schluessel(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function ausSchluessel(tag: string): Date {
  const [j, m, t] = tag.split("-").map(Number)
  return new Date(j, m - 1, t)
}

interface Props {
  /** gewählter Tag (YYYY-MM-DD) oder null */
  wert: string | null
  onWahl: (tag: string | null) => void
  /** Tage mit Aktivität (YYYY-MM-DD) — bekommen einen Punkt */
  markiert?: Set<string>
}

export function Kalender({ wert, onWahl, markiert }: Props) {
  const [offen, setOffen] = useState(false)
  const heute = new Date()
  const heuteSchluessel = schluessel(heute)
  const [blatt, setBlatt] = useState(() => {
    const d = wert ? ausSchluessel(wert) : heute
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })

  // Beim Öffnen das Blatt des gewählten Tags zeigen
  useEffect(() => {
    if (!offen) return
    const d = wert ? ausSchluessel(wert) : new Date()
    setBlatt(new Date(d.getFullYear(), d.getMonth(), 1))
  }, [offen, wert])

  // Schließen bei Klick daneben oder Escape
  const boxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!offen) return
    const klick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOffen(false)
    }
    const taste = (e: KeyboardEvent) => e.key === "Escape" && setOffen(false)
    document.addEventListener("mousedown", klick)
    document.addEventListener("keydown", taste)
    return () => {
      document.removeEventListener("mousedown", klick)
      document.removeEventListener("keydown", taste)
    }
  }, [offen])

  // Raster: Montag vor dem Monatsersten bis Sonntag nach dem Monatsletzten
  const erster = new Date(blatt)
  const versatz = (erster.getDay() + 6) % 7 // Mo = 0
  const start = new Date(erster)
  start.setDate(start.getDate() - versatz)
  const tage = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return d
  })
  const naechstesBlattGesperrt =
    blatt.getFullYear() > heute.getFullYear() ||
    (blatt.getFullYear() === heute.getFullYear() && blatt.getMonth() >= heute.getMonth())

  const waehlen = (tag: string | null) => {
    onWahl(tag)
    setOffen(false)
  }

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={offen}
        className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-[12.5px] font-semibold tabular-nums hover:border-primary"
      >
        <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
        {wert ? _anzeige.format(ausSchluessel(wert)) : "Tag wählen"}
      </button>

      {offen && (
        <div
          role="dialog"
          aria-label="Tag auswählen"
          className="absolute left-0 top-full z-40 mt-1.5 w-[17.5rem] rounded-xl border bg-card p-3 shadow-card"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              aria-label="Monat davor"
              onClick={() => setBlatt(new Date(blatt.getFullYear(), blatt.getMonth() - 1, 1))}
              className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-[13px] font-bold">{_monat.format(blatt)}</span>
            <button
              type="button"
              aria-label="Monat danach"
              disabled={naechstesBlattGesperrt}
              onClick={() => setBlatt(new Date(blatt.getFullYear(), blatt.getMonth() + 1, 1))}
              className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center">
            {WOCHENTAGE.map((w) => (
              <span key={w} className="py-1 text-[10.5px] font-bold uppercase text-muted-foreground">
                {w}
              </span>
            ))}
            {tage.map((d) => {
              const s = schluessel(d)
              const imMonat = d.getMonth() === blatt.getMonth()
              const zukunft = s > heuteSchluessel
              const gewaehlt = s === wert
              const istHeute = s === heuteSchluessel
              return (
                <button
                  key={s}
                  type="button"
                  disabled={zukunft}
                  onClick={() => waehlen(s)}
                  className={`relative flex h-8 items-center justify-center rounded-md text-[12.5px] tabular-nums transition-colors disabled:cursor-not-allowed disabled:opacity-25 ${
                    gewaehlt
                      ? "bg-primary font-bold text-primary-foreground"
                      : `hover:bg-secondary ${imMonat ? "text-foreground" : "text-muted-foreground/50"} ${
                          istHeute ? "ring-1 ring-primary" : ""
                        }`
                  }`}
                >
                  {d.getDate()}
                  {markiert?.has(s) && (
                    <span
                      className={`absolute bottom-1 h-1 w-1 rounded-full ${
                        gewaehlt ? "bg-primary-foreground" : "bg-primary"
                      }`}
                    />
                  )}
                </button>
              )
            })}
          </div>

          <div className="mt-2 flex items-center justify-between border-t pt-2">
            <button
              type="button"
              onClick={() => waehlen(heuteSchluessel)}
              className="rounded-md px-2 py-1 text-[12px] font-semibold text-primary hover:bg-secondary"
            >
              Heute
            </button>
            <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <span className="h-1 w-1 rounded-full bg-primary" /> Aktivität
            </span>
            <button
              type="button"
              onClick={() => waehlen(null)}
              className="rounded-md px-2 py-1 text-[12px] font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              Alle Tage
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
